import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { LogOut, Radio, ServerCog, SlidersHorizontal, UserRoundCog } from 'lucide-react';
import { Avatar } from './Avatar';
import './AvatarMenu.css';

export type SettingsTab = 'profile' | 'playback' | 'podcasts' | 'interface' | 'library' | 'users' | 'email' | 'network';

interface Props {
    anchorRect: DOMRect;
    userId?: number;
    avatarVersion?: number;
    userName: string;
    userHandle: string;
    onSelectTab: (tab: SettingsTab) => void;
    onLogout: () => void;
    onClose: () => void;
}

const MENU_ITEMS: { label: string; icon: typeof UserRoundCog; tab: SettingsTab; badge?: string }[] = [
    { label: 'Account settings', icon: UserRoundCog, tab: 'profile' },
    { label: 'Playback', icon: SlidersHorizontal, tab: 'playback' },
    { label: 'Podcasts', icon: Radio, tab: 'podcasts' },
    { label: 'Server settings', icon: ServerCog, tab: 'library', badge: 'ADMIN' },
];

export function AvatarMenu({
    anchorRect,
    userId,
    avatarVersion,
    userName,
    userHandle,
    onSelectTab,
    onLogout,
    onClose,
}: Props): React.JSX.Element {
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handlePointerDown = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const style: React.CSSProperties = {
        left: anchorRect.left,
        bottom: window.innerHeight - anchorRect.top + 8,
        width: anchorRect.width,
    };

    return createPortal(
        <div className="avatarMenu" style={style} ref={menuRef}>
            <div className="avatarMenuHeader">
                <Avatar userId={userId} avatarVersion={avatarVersion} className="avatarMenuAvatar" />
                <div className="avatarMenuIdentity">
                    <div className="avatarMenuName">{userName}</div>
                    <div className="avatarMenuHandle">{userHandle}</div>
                </div>
            </div>
            <div className="avatarMenuDivider" />
            {MENU_ITEMS.map(({ label, icon: Icon, tab, badge }) => (
                <button
                    key={tab}
                    className="avatarMenuItem"
                    onClick={() => {
                        onSelectTab(tab);
                        onClose();
                    }}
                >
                    <Icon size={18} strokeWidth={2.75} />
                    <span className="avatarMenuItemLabel">{label}</span>
                    {badge && <span className="avatarMenuBadge">{badge}</span>}
                </button>
            ))}
            <button
                className="avatarMenuItem avatarMenuItemDanger"
                onClick={() => {
                    onClose();
                    onLogout();
                }}
            >
                <LogOut size={18} strokeWidth={2.75} />
                <span className="avatarMenuItemLabel">Log out</span>
            </button>
        </div>,
        document.body
    );
}
